/**
 * Application-wide color constants
 * Use these values consistently across the platform
 */

export const colors = {
  // Text colors
  text: {
    default: '#222225',      // Default text color for unselected items
    selected: '#0B0909',     // Text color for selected/active items - also for titles/headers
    primary: '#0B0909',      // Primary text color (same as selected for consistency)
    secondary: '#374151',    // Secondary/muted text (Gray-700 for WCAG AA compliance - 4.5:1 contrast)
    disabled: '#9CA3AF',     // Disabled text color (Gray-400)
    clickable: '#4B5563',    // Clickable text/links default state - also for details/smaller info (Gray-600 for better contrast)
    clickableHover: '#0B0909', // Clickable text/links hover state
  },

  // Background colors
  background: {
    default: '#FFFFFF',      // Default background (white)
    primary: '#FFFFFF',      // Primary background (white)
    secondary: '#F9FAFB',    // Secondary background (light gray)
    tertiary: '#F3F4F6',     // Tertiary background (slightly darker gray)
    paper: '#FFFFFF',        // Paper/card background (white)
    hover: '#F3F4F6',        // Hover state background
  },

  // Interactive states
  interactive: {
    default: '#222225',
    active: '#0B0909',
    hover: '#0B0909',
  },

  // Button states
  button: {
    inactive: '#6B7280',
    active: '#0B0909',
    activeHover: '#000000',
    inactiveHover: '#4B5563',
  },

  // Borders and dividers
  border: {
    // 0.42 (#949494 on white) is the lightest black alpha whose border is
    // 3.03:1 on white (ruling 2026-09-30; 0.12 was 1.32:1), so a field whose
    // outline reads this token - StyledSelect, StyledAutocomplete and the other
    // borders drawn from it - meets WCAG 2.1 1.4.11 (3:1). A plain MUI
    // TextField keeps MUI's own outline (0.23) and is not covered by this
    // token. Hover stays darker than rest, below the focused black.
    default: 'rgba(0, 0, 0, 0.42)',
    light: 'rgba(0, 0, 0, 0.08)',
    hover: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.06)',
    status: '#0B0909',        // Status indicator border
  },

  // Compliance meter colors: the status light values (D-DS-01 lists the
  // meter among its failing entries), text-grade on white.
  complianceMeter: {
    excellent: '#047857',  // Green (90-100%), 5.48:1
    good: '#B45309',       // Amber (70-89%), 5.02:1
    poor: '#B91C1C',       // Red (< 70%), 6.47:1
  },

  // Status colors. Light-mode values that pass WCAG 2.1 AA as TEXT (1.4.3,
  // 4.5:1) on white and on background.secondary, and so also as icons and
  // borders (1.4.11, 3:1); white text ON them passes too (D-DS-01 addendum,
  // 2026-09-30: light values now, the mode-aware layer stays owed).
  status: {
    success: '#047857',    // Emerald-700: 5.48:1 on white; white on it 5.48:1
    // Hover/pressed surfaces under white text: darker than the rest colour,
    // never lighter, so a hovered button keeps its contrast.
    successHover: '#065F46', // Emerald-800: white on it 7.68:1
    errorHover: '#991B1B',   // Red-800: white on it 8.31:1
    warning: '#B45309',    // Amber-700: 5.02:1 on white
    error: '#B91C1C',      // Red-700: 6.47:1 on white
    info: '#1D4ED8',       // Blue-700: 6.70:1 on white
  },

  // Primary color palette (for UI accents)
  primary: {
    light: '#E3F2FD',      // Light blue background
    main: '#1976d2',       // MUI default primary blue
    dark: '#1565c0',       // Darker blue for hover/active
  },
} as const;

export default colors;
